import {ItemListViewProps} from "../../types/ItemListViewProps.ts";
import HnCarouselHolder from "./HnCarouselStyles.tsx";
import {truncateWords} from "../../utils/search-utils.ts";
import GetLabel from "../../language/GetLabel.tsx";
import {getLabel} from "../../language/lang-utils.ts";
import {useEffect} from 'react';
import {resizeElement} from "../../utils/responsive.ts";
import {loadAppropriateImages} from "../../utils/image-utils.ts";
import { getLanguage } from "../../language/lang-utils.ts";
import { DateTime } from "luxon";

const HnCarousel = (props: ItemListViewProps) => {
    const itemList = props.itemList;
    if (!itemList) return;
    const lang = getLanguage();

    useEffect(() => {
        resizeElement('.carousel-img');
        loadAppropriateImages();
    });

    const isArticle: boolean = document.querySelector('.article-holder') !== null;

    return (
        <HnCarouselHolder>
            <section className="hn__content" itemScope={true} itemType='https://schema.org/WPSideBar' aria-label={itemList.title}>
                <h2 id="hn-header" className="hn__Header" itemProp='name'>
                    {itemList.title}
                </h2>
                {itemList.seeallbtn && // only if see all button is checked, show the button/link
                    <a href={itemList.seeAllUrl} className="seeAllBtn" aria-label={itemList.title ? getLabel("SEEALL") + " " + itemList.title : getLabel("SEEALL")}>
                        <GetLabel name="SEEALL"/>
                        <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="arrow-right" className="svg-inline--fa fa-arrow-right fa-w-14 " role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                            <path fill="currentColor" d="M190.5 66.9l22.2-22.2c9.4-9.4 24.6-9.4 33.9 0L441 239c9.4 9.4 9.4 24.6 0 33.9L246.6 467.3c-9.4 9.4-24.6 9.4-33.9 0l-22.2-22.2c-9.5-9.5-9.3-25 .4-34.3L311.4 296H24c-13.3 0-24-10.7-24-24v-32c0-13.3 10.7-24 24-24h287.4L190.9 101.2c-9.8-9.3-10-24.8-.4-34.3z"></path>
                        </svg>
                    </a>
                }
                <ul itemScope={true} itemType="https://schema.org/ItemList" role="list">
                    {itemList.items.map((item, i) => {
                        if (item.image == undefined || item.title === undefined) return;

                        const itemLiveTimeEN = item.liveDateTime && item.type == 'video' ? item.liveDateTime.toUTC().setLocale('en') : item.liveDateTime && (item.type == 'live' || item.type == 'prelive') ? item.liveDateTime.setLocale('en') : "";
                        const itemLiveTimeFR = item.liveDateTime && item.type == 'video' ? item.liveDateTime.toUTC().setLocale('fr') : item.liveDateTime && (item.type == 'live' || item.type == 'prelive') ? item.liveDateTime.setLocale('fr') : "";

                        const liveDateTimeEn = String(itemLiveTimeEN?.toLocaleString(DateTime.DATE_FULL))
                        const liveDateTimeFr = String(itemLiveTimeFR?.toLocaleString(DateTime.DATE_FULL))
                        
                        return (
                            <li key={i} className="hn-carousel-item" itemProp="itemListElement">
                                <div className="header__container">
                                    <a href={item.url} target={item.openInNewTab ? "_blank" : "_self"} itemProp='url' tabIndex={-1}>
                                        <img loading="lazy" className="multi-thumb" data-src={item.image} alt={item.altImageTxt ? item.altImageTxt : item.title + " - " + item.category} itemProp={'image'}/>
                                    </a>
                                    {item.testStream === "true" && <div className='testFlag'>TEST</div>}
                                </div>
                                <div className="description__container" tabIndex={-1}>
                                    {(!isArticle && item.category && item.title) ?
                                        <a href={item.url} target={item.openInNewTab ? "_blank" : "_self"} aria-label={item.category && item.title ? item.title + " - " + item.category : ""}>
                                            <p className="series-title" itemProp='subjectOf'>{item.category}</p>
                                            {item.testStream !== "true" && (
                                                <div className="episode-date">
                                                {item.liveDateTime && lang == "en" && item.title != liveDateTimeEn && !item.title.includes(liveDateTimeEn) ? liveDateTimeEn : item.liveDateTime && lang == "fr" && !item.title.includes("1er") && item.title != liveDateTimeFr && !item.title.includes(liveDateTimeFr) ? liveDateTimeFr : ""}
                                                </div>
                                            )}
                                            <p className="episode-title" itemProp='description'>
                                            {item.title && truncateWords(item.title, 225)}
                                            {item.title && item.title.length > 225 && (
                                                <span>...</span>
                                            )}
                                            </p>
                                        </a>
                                    :
                                        <>
                                        <a href={item.categoryURL} target={item.openInNewTab ? "_blank" : "_self"}>
                                            <p className="series-title" itemProp='subjectOf'>{item.category}</p>
                                        </a>
                                        <a href={item.url} itemProp='url' aria-label={item.category && item.title ? item.title + " - " + item.category : ""}>
                                            <p className="episode-title" itemProp='description'>
                                                {item.title && truncateWords(item.title, 225)}
                                                {item.title && item.title.length > 225 && (
                                                    <span>...</span>
                                                )}
                                            </p>
                                        </a>
                                        </>
                                    }

                                </div>
                            </li>
                        )
                    })}
                </ul>
                {itemList.items.length > 4 && itemList.seeallbtn && // only if items are more than 4 and see all button is checked, show the banner/button/link
                    <span className="hn__Header-bottom">
                        <a href={itemList.seeAllUrl} className="seeAllBtn" aria-label={itemList.title ? getLabel("SEEALL") + " " + itemList.title : getLabel("SEEALL")}>
                            <GetLabel name="SEEALL"/>
                            <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="arrow-right" className="svg-inline--fa fa-arrow-right fa-w-14 " role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                                <path fill="currentColor" d="M190.5 66.9l22.2-22.2c9.4-9.4 24.6-9.4 33.9 0L441 239c9.4 9.4 9.4 24.6 0 33.9L246.6 467.3c-9.4 9.4-24.6 9.4-33.9 0l-22.2-22.2c-9.5-9.5-9.3-25 .4-34.3L311.4 296H24c-13.3 0-24-10.7-24-24v-32c0-13.3 10.7-24 24-24h287.4L190.9 101.2c-9.8-9.3-10-24.8-.4-34.3z"></path>
                            </svg>
                        </a>
                    </span>
                }
            </section>
        </HnCarouselHolder>
    );
};

export default HnCarousel;